import { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { routerPathsFor, type TRouterPaths } from "@/ui/router";
import {
    LayoutGrid,
    FileText,
    Settings,
    LogOut,
    Users,
    Building2,
    Repeat,
    Briefcase,
    Megaphone,
    BookOpen,
    Upload,
    Import,
    SlidersHorizontal,
    MapPin,
    Layers,
    ScrollText,
    Send,
    Mail,
    LifeBuoy,
    Inbox,
    ChevronsLeft,
    ChevronsRight,
    type LucideIcon,
} from "lucide-react";
import { cn } from "@/utils/utils";
import { Logo } from "@/ui/atoms";
import { CollapsibleNavSection } from "@/ui/molecules";
import type { TAvailableCompany, TCompanyFeatureFlags } from "@/store";

type TNavItem = {
    label: string;
    path: string;
    icon: LucideIcon;
};

type TSettingsNavItem = TNavItem & {
    requiredAction: string;
};

// Company admin items (visible to company admins)
const companyAdminItems = (paths: TRouterPaths): TNavItem[] => [
    { label: "Users", path: paths.admin.users, icon: Users },
    { label: "Business Partners", path: paths.admin.businessPartners, icon: Briefcase },
    { label: "Announcements", path: paths.admin.announcements, icon: Megaphone },
    { label: "Email Communications", path: paths.admin.emailCommunications, icon: Mail },
];

// Settings items — each gated by a per-item page-access action
const settingsItems = (paths: TRouterPaths): TSettingsNavItem[] => [
    {
        label: "Books",
        path: paths.settings.books,
        icon: BookOpen,
        requiredAction: "settings_books_page_access",
    },
    {
        label: "Chart of Accounts",
        path: paths.settings.chartOfAccounts,
        icon: SlidersHorizontal,
        requiredAction: "settings_coa_view",
    },
    {
        label: "Propcodes",
        path: paths.settings.propcodes,
        icon: MapPin,
        requiredAction: "settings_propcodes_view",
    },
    {
        label: "Segments",
        path: paths.settings.segments,
        icon: Layers,
        requiredAction: "settings_segments_master_page_access",
    },
    {
        label: "Import",
        path: paths.settings.yardiImport,
        icon: Import,
        requiredAction: "settings_yardi_import_page_access",
    },
];

// Global admin items (requires viewing as Proda company)
const globalAdminItems: TNavItem[] = [
    { label: "Companies", path: "/admin/companies", icon: Building2 },
    { label: "Global Audit Log", path: "/admin/global-audit-log", icon: ScrollText },
    { label: "Forwarded Tickets", path: "/admin/support-forwarded", icon: Inbox },
];

// Inline switcher with optional custom trigger; dropdown has large, well-spaced options
function InlineSwitcher<T extends string>({
    options,
    value,
    onChange,
    renderTrigger,
}: {
    options: { id: T; label: string }[];
    value: T | null;
    onChange: (value: T | null) => void;
    renderTrigger?: (props: { onClick: () => void }) => React.ReactNode;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const toggle = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setIsOpen((prev) => !prev);
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    return (
        <div className="relative inline-flex min-w-0 max-w-full" ref={ref}>
            {renderTrigger ? (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={toggle}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggle()}
                    className="inline-flex min-w-0 max-w-full items-center flex-nowrap cursor-pointer rounded hover:bg-sidebar-muted text-sidebar-muted-foreground hover:text-primary transition-colors outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent focus-visible:ring-offset-1"
                >
                    {renderTrigger({ onClick: toggle })}
                </div>
            ) : (
                <button
                    onClick={toggle}
                    className="p-1.5 rounded hover:bg-sidebar-muted text-sidebar-muted-foreground hover:text-primary transition-colors"
                    title="Switch"
                >
                    <Repeat className="h-4 w-4" />
                </button>
            )}
            {isOpen && (
                <div
                    className="fixed w-64 bg-white rounded-lg shadow-lg border border-slate-200 py-2"
                    style={{
                        zIndex: 9999,
                        bottom: ref.current
                            ? window.innerHeight - ref.current.getBoundingClientRect().top + 4
                            : "auto",
                        left: ref.current ? ref.current.getBoundingClientRect().left : "auto",
                    }}
                >
                    {options.map((opt) => (
                        <button
                            key={opt.id}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange(value === opt.id ? null : opt.id);
                                setIsOpen(false);
                            }}
                            className={cn(
                                "w-full text-left px-4 py-2.5 text-sm min-h-[2.5rem] flex items-center break-words hover:bg-slate-100 transition-colors",
                                value === opt.id && "bg-slate-100 font-medium",
                            )}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// Flat (non-section) nav link. Renders icon-only with a tooltip when collapsed.
function SideNavLink({
    to,
    icon: Icon,
    label,
    collapsed = false,
    "data-testid": dataTestId,
}: {
    to: string;
    icon: LucideIcon;
    label: string;
    collapsed?: boolean;
    "data-testid"?: string;
}) {
    return (
        <NavLink
            to={to}
            title={collapsed ? label : undefined}
            data-testid={dataTestId}
            className={({ isActive }) =>
                cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                    collapsed && "justify-center px-0",
                    isActive
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground",
                )
            }
        >
            <Icon className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>{label}</span>}
        </NavLink>
    );
}

export interface TSidebarProps {
    userName?: string;
    companyName?: string;
    companyLogoUrl?: string | null;
    isAdmin?: boolean;
    isProdaUser?: boolean;
    /** Feature flags of the user's own company (Proda users bypass flag gating). */
    companyFeatureFlags?: TCompanyFeatureFlags;
    permissions?: string[];
    emulationTargets?: string[];
    subgroupLabels?: Record<string, string>;
    effectiveSubgroup?: string | null;
    userSubgroup?: string | null;
    effectiveCompanyId?: string | null;
    availableCompanies?: TAvailableCompany[];
    onCompanySwitch?: (companyId: string | null) => void;
    onSubgroupSwitch?: (subgroup: string | null) => void;
    onSettingsClick?: () => void;
    onLogout?: () => void;
    /** Base path of the acting company's customer module pages (`""` for Rev EU). */
    customerModuleBasePath?: string;
    className?: string;
}

/**
 * PRODA Sidebar Component
 *
 * Light sidebar matching PRODA brand guidelines
 * Uses a clean white background with blue accents
 */
export function Sidebar({
    userName,
    companyName,
    companyLogoUrl,
    isAdmin,
    isProdaUser,
    companyFeatureFlags,
    permissions = [],
    emulationTargets = [],
    subgroupLabels = {},
    effectiveSubgroup,
    userSubgroup,
    effectiveCompanyId,
    availableCompanies = [],
    onCompanySwitch,
    onSubgroupSwitch,
    onSettingsClick,
    onLogout,
    customerModuleBasePath = "",
    className,
}: TSidebarProps) {
    const { pathname } = useLocation();
    const paths = routerPathsFor(customerModuleBasePath);

    // The currently active subgroup (emulated or real)
    const currentSubgroup = effectiveSubgroup ?? userSubgroup ?? null;

    // Get the display company name (effective or actual)
    const displayCompanyName = effectiveCompanyId
        ? availableCompanies.find((t) => t.id === effectiveCompanyId)?.name || companyName
        : companyName;

    // Get the effective company (for checking if it's Proda)
    const effectiveCompany = effectiveCompanyId
        ? availableCompanies.find((t) => t.id === effectiveCompanyId)
        : null;
    const currentCompanyName = effectiveCompany?.name || companyName;
    const currentCompanyCode = effectiveCompany?.code || null;

    // Get the display company logo (effective or actual)
    const displayCompanyLogoUrl = effectiveCompanyId
        ? availableCompanies.find((t) => t.id === effectiveCompanyId)?.logoUrl || null
        : companyLogoUrl;

    // Permission-based gates
    // Homepage v2 needs both the permission (every subgroup holds it) and the
    // company's rollout flag, so a company still on the legacy file lists sees
    // no Home entry at all.
    const canViewHomepage =
        permissions.includes("homepage_page_access") &&
        Boolean(companyFeatureFlags?.homepage_v2_enabled);
    const canUploadFinJournals = permissions.includes("finjournal_file_upload");
    const canUploadTBGLAndSupporting = permissions.includes("tbgl_file_upload");
    // Business Partner / BP+ only: no other subgroup holds supporting_file_upload.
    const canUploadSupporting = permissions.includes("supporting_file_upload");
    const canAccessBusinessPartners = permissions.includes("business_partners_page_access");
    const canViewUsers = permissions.includes("users_view");
    const canManageAnnouncements = permissions.includes("announcement_page_access");
    const canViewSupport = permissions.includes("report_view");

    const showUploadSection =
        canUploadFinJournals || canUploadTBGLAndSupporting || canUploadSupporting;

    const canViewAuditLog = permissions.includes("audit_log_view");
    // Email Communications needs the permission AND (for non-Proda users) the
    // company feature flag. Proda users always see it, whichever company they
    // are viewing as.
    const canViewEmailLog =
        permissions.includes("email_log_view") &&
        (Boolean(isProdaUser) || Boolean(companyFeatureFlags?.email_communications_enabled));
    // With Homepage v2 the board's "All submissions" link replaces the All
    // Files and TB/GL list entries, so they only show on the legacy landing.
    const canViewAllFiles = !canViewHomepage && permissions.includes("files_all_page_access");
    const canViewTBGLFiles = !canViewHomepage && permissions.includes("tbgl_files_page_access");
    const canViewFinJournals = permissions.includes("finjournal_files_view");
    // Every file page belongs to a customer product module. A company whose
    // customer module has none (or no customer module at all) gets no file
    // permissions from /auth/me, so the whole section disappears.
    const canViewAnyFiles = canViewAllFiles || canViewTBGLFiles || canViewFinJournals;
    // Admins keep the section for Archive even when the board replaces the
    // lists, but only if the company's module has file pages at all.
    const holdsFilePagePermission =
        permissions.includes("files_all_page_access") ||
        permissions.includes("tbgl_files_page_access") ||
        canViewFinJournals;

    // Check if user is from Proda company (by name or code)
    const isProdaCompany =
        currentCompanyName?.toLowerCase() === "proda" ||
        currentCompanyCode?.toUpperCase() === "PRODA";

    // Can switch subgroup: based on the REAL ability to emulate (emulationTargets,
    // which the backend always derives from the real subgroup). Permissions can be
    // de-escalated while emulating, so the switcher must not gate on them — otherwise
    // emulating a subgroup without role_emulate would hide the control to un-emulate.
    const canSwitchSubgroup = emulationTargets.length > 0;

    // Can switch companies: Proda users (view-as any company) and members of
    // several companies (their own memberships only — availableCompanies is
    // then exactly that list, and switching needs at least two entries).
    const canSwitchCompany = isProdaUser
        ? availableCompanies.length > 0
        : availableCompanies.length > 1;

    // Admin chrome is shown to real admins regardless of emulation
    const isViewingAsAdmin = isAdmin;

    // Show admin views only when viewing as admin
    const showAdminViews = isViewingAsAdmin;

    // Per-item settings nav visibility — each item requires its own page-access permission
    const visibleSettingsItems = settingsItems(paths).filter((item) =>
        permissions.includes(item.requiredAction),
    );

    // Prepare company options for switcher (only active companies)
    const companyOptions = availableCompanies
        .filter((t) => t.isActive)
        .map((t) => ({
            id: t.id,
            label: `${t.name} (${t.code})`,
        }));

    // Subgroup options: the user's own (real) subgroup first — selecting it
    // returns home / stops emulating — followed by the emulation targets. Without
    // the home entry a Product Owner (who cannot emulate itself) has no way back.
    const subgroupOptions = [
        ...(userSubgroup
            ? [{ id: userSubgroup, label: subgroupLabels[userSubgroup] ?? userSubgroup }]
            : []),
        ...emulationTargets.map((target) => ({
            id: target,
            label: subgroupLabels[target] ?? target,
        })),
    ];

    // Display label for the current subgroup
    const currentSubgroupLabel = currentSubgroup
        ? (subgroupLabels[currentSubgroup] ?? currentSubgroup)
        : "User";

    // Collapsible sections state
    const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
        const saved =
            typeof window !== "undefined"
                ? window.sessionStorage.getItem("sidebar-expanded-sections")
                : null;
        return saved ? new Set(JSON.parse(saved)) : new Set(["upload", "files"]);
    });

    // Whole-sidebar collapse state — persisted across sessions
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        const saved =
            typeof window !== "undefined" ? window.localStorage.getItem("sidebar-collapsed") : null;
        return saved === "true";
    });

    const setCollapsed = (next: boolean) => {
        setIsCollapsed(next);
        if (typeof window !== "undefined") {
            window.localStorage.setItem("sidebar-collapsed", String(next));
        }
    };

    const toggleCollapsed = () => setCollapsed(!isCollapsed);

    const toggleSection = (section: string) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(section)) {
                next.delete(section);
            } else {
                next.add(section);
            }
            if (typeof window !== "undefined") {
                window.sessionStorage.setItem(
                    "sidebar-expanded-sections",
                    JSON.stringify([...next]),
                );
            }
            return next;
        });
    };

    return (
        <aside
            data-testid="app-sidebar"
            className={cn(
                "flex h-full flex-col bg-sidebar text-sidebar-foreground",
                isCollapsed ? "w-16" : "w-64",
                className,
            )}
        >
            {/* Logo + collapse toggle */}
            <div
                className={cn(
                    "flex h-16 shrink-0 items-center",
                    isCollapsed ? "justify-center gap-1 px-1" : "justify-between px-6",
                )}
            >
                {isCollapsed ? (
                    <svg
                        viewBox="0 0 65 98"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-auto"
                        aria-hidden="true"
                    >
                        <path
                            d="M0 24.8106V78.5969L29.3808 97.5671V5.84045L0 24.8106Z"
                            fill="#0018EA"
                        />
                        <path
                            d="M64.3016 18.9248V53.7411L34.9209 72.7112V0L64.3016 18.9248Z"
                            fill="#0018EA"
                        />
                    </svg>
                ) : (
                    <Logo
                        size="sm"
                        src={displayCompanyLogoUrl || undefined}
                        alt={displayCompanyLogoUrl ? `${displayCompanyName} logo` : undefined}
                        className="h-8 w-auto"
                    />
                )}
                <button
                    onClick={toggleCollapsed}
                    aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    aria-expanded={!isCollapsed}
                    title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    className="p-1.5 rounded-lg text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground transition-colors"
                >
                    {isCollapsed ? (
                        <ChevronsRight className="h-5 w-5" />
                    ) : (
                        <ChevronsLeft className="h-5 w-5" />
                    )}
                </button>
            </div>

            {/* Navigation */}
            <nav className="scrollbar-hover min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden whitespace-nowrap p-4">
                {/* Home — shown once the company has the Homepage v2 flag on
                    (every subgroup holds the permission itself). */}
                {canViewHomepage && (
                    <SideNavLink
                        to={paths.home}
                        icon={LayoutGrid}
                        label="Home"
                        collapsed={isCollapsed}
                        data-testid="nav-home"
                    />
                )}

                {/* Upload Section - conditional based on permissions */}
                {showUploadSection && (
                    <CollapsibleNavSection
                        title="Upload"
                        icon={Upload}
                        isExpanded={expandedSections.has("upload")}
                        onToggle={() => toggleSection("upload")}
                        collapsed={isCollapsed}
                        active={pathname.startsWith(`${customerModuleBasePath}/upload`)}
                    >
                        {canUploadFinJournals && (
                            <NavLink
                                to={paths.upload.finJournals}
                                className={({ isActive }) =>
                                    cn(
                                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                                        isActive
                                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                            : "text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground",
                                    )
                                }
                            >
                                FinJournals
                            </NavLink>
                        )}
                        {canUploadTBGLAndSupporting && (
                            <NavLink
                                to={paths.upload.tbgl}
                                className={({ isActive }) =>
                                    cn(
                                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                                        isActive
                                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                            : "text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground",
                                    )
                                }
                            >
                                Financial TB/GL
                            </NavLink>
                        )}
                        {canUploadSupporting && (
                            <NavLink
                                to={paths.upload.supporting}
                                className={({ isActive }) =>
                                    cn(
                                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                                        isActive
                                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                            : "text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground",
                                    )
                                }
                            >
                                Supporting Files
                            </NavLink>
                        )}
                    </CollapsibleNavSection>
                )}

                {/* Financial Files Section */}
                {(canViewAnyFiles || (showAdminViews && holdsFilePagePermission)) && (
                    <CollapsibleNavSection
                        title="Financial Files"
                        icon={FileText}
                        isExpanded={expandedSections.has("files")}
                        onToggle={() => toggleSection("files")}
                        collapsed={isCollapsed}
                        active={pathname.startsWith(paths.files.list)}
                    >
                        {canViewAllFiles && (
                            <NavLink
                                to={paths.files.list}
                                end
                                data-testid="nav-all-files"
                                className={({ isActive }) =>
                                    cn(
                                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                                        isActive
                                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                            : "text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground",
                                    )
                                }
                            >
                                All Files
                            </NavLink>
                        )}
                        {canViewTBGLFiles && (
                            <NavLink
                                to={paths.files.tbgl}
                                data-testid="nav-tbgl-files"
                                className={({ isActive }) =>
                                    cn(
                                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                                        isActive
                                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                            : "text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground",
                                    )
                                }
                            >
                                Financial TB/GL
                            </NavLink>
                        )}
                        {canViewFinJournals && (
                            <NavLink
                                to={paths.files.finJournals}
                                data-testid="nav-finjournals"
                                className={({ isActive }) =>
                                    cn(
                                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                                        isActive
                                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                            : "text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground",
                                    )
                                }
                            >
                                FinJournals
                            </NavLink>
                        )}
                        {showAdminViews && (
                            <NavLink
                                to={paths.files.archive}
                                className={({ isActive }) =>
                                    cn(
                                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                                        isActive
                                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                            : "text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground",
                                    )
                                }
                            >
                                Archive
                            </NavLink>
                        )}
                    </CollapsibleNavSection>
                )}

                {/* Support - Report-from-user page (report_view, Product Owner only) */}
                {canViewSupport && (
                    <SideNavLink
                        to="/support"
                        icon={LifeBuoy}
                        label="Support"
                        collapsed={isCollapsed}
                    />
                )}

                {/* Audit Log - visible to users with audit_log_view permission */}
                {canViewAuditLog && (
                    <SideNavLink
                        to="/admin/audit-log"
                        icon={ScrollText}
                        label="Audit Log"
                        collapsed={isCollapsed}
                        data-testid="nav-audit-log"
                    />
                )}

                {/* Business Partners - non-admin roles with BP list access reach
                    the page here; admins see it under Company Admin below */}
                {!showAdminViews && canAccessBusinessPartners && (
                    <SideNavLink
                        to={paths.admin.businessPartners}
                        icon={Briefcase}
                        label="Business Partners"
                        collapsed={isCollapsed}
                        data-testid="nav-business-partners"
                    />
                )}

                {/* Company Admin section - visible to admins */}
                {showAdminViews && (
                    <div className={cn(isCollapsed ? "space-y-1" : "mt-8 pt-4")}>
                        {!isCollapsed && (
                            <p className="px-3 text-xs font-semibold text-sidebar-muted-foreground uppercase tracking-wider mb-2">
                                Company Admin
                            </p>
                        )}
                        {companyAdminItems(paths)
                            .filter((item) => {
                                if (item.path === paths.admin.businessPartners) {
                                    return canAccessBusinessPartners;
                                }
                                if (item.path === paths.admin.users) {
                                    return canViewUsers;
                                }
                                if (item.path === paths.admin.announcements) {
                                    return canManageAnnouncements;
                                }
                                if (item.path === paths.admin.emailCommunications) {
                                    return canViewEmailLog;
                                }
                                return true;
                            })
                            .map((item) => {
                                let testId: string | undefined;
                                if (item.label === "Users") testId = "nav-users";
                                else if (item.label === "Business Partners")
                                    testId = "nav-business-partners";
                                else if (item.label === "Email Communications")
                                    testId = "nav-email-communications";
                                return (
                                    <SideNavLink
                                        key={item.path}
                                        to={item.path}
                                        icon={item.icon}
                                        label={item.label}
                                        collapsed={isCollapsed}
                                        data-testid={testId}
                                    />
                                );
                            })}
                    </div>
                )}

                {/* File Submissions - SFTP submission configuration and activity */}
                {permissions.includes("settings_company_page_access") && (
                    <div className={cn(!isCollapsed && "mt-4")}>
                        <SideNavLink
                            to={paths.admin.submissions}
                            icon={Send}
                            label="File Submissions"
                            collapsed={isCollapsed}
                            data-testid="nav-file-submissions"
                        />
                    </div>
                )}

                {/* Settings section - collapsible, visible when user has access to at least one settings area */}
                {visibleSettingsItems.length > 0 && (
                    <div className={cn(!isCollapsed && "mt-4")}>
                        <CollapsibleNavSection
                            title="Master Data"
                            icon={SlidersHorizontal}
                            isExpanded={expandedSections.has("settings")}
                            onToggle={() => toggleSection("settings")}
                            collapsed={isCollapsed}
                            active={pathname.startsWith(`${customerModuleBasePath}/settings`)}
                        >
                            {visibleSettingsItems.map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={({ isActive }) =>
                                        cn(
                                            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                                            isActive
                                                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                                : "text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground",
                                        )
                                    }
                                >
                                    <item.icon className="h-4 w-4" />
                                    {item.label}
                                </NavLink>
                            ))}
                        </CollapsibleNavSection>
                    </div>
                )}

                {/* Global Admin section - visible only when viewing as Proda company */}
                {showAdminViews && isProdaCompany && (
                    <div className={cn(isCollapsed ? "space-y-1" : "mt-6 pt-4")}>
                        {!isCollapsed && (
                            <p className="px-3 text-xs font-semibold text-sidebar-muted-foreground uppercase tracking-wider mb-2">
                                Global Admin
                            </p>
                        )}
                        {globalAdminItems
                            .filter((item) =>
                                item.path === "/admin/support-forwarded" ? canViewSupport : true,
                            )
                            .map((item) => (
                                <SideNavLink
                                    key={item.path}
                                    to={item.path}
                                    icon={item.icon}
                                    label={item.label}
                                    collapsed={isCollapsed}
                                />
                            ))}
                    </div>
                )}
            </nav>

            {/* User section - compact with inline switchers */}
            {isCollapsed ? (
                <div className="shrink-0 p-2 border-t border-sidebar-muted flex justify-center">
                    <button
                        onClick={() => setCollapsed(false)}
                        title={`${userName || "User"} — expand sidebar`}
                        aria-label={`Expand sidebar (${userName || "User"})`}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-sidebar-accent text-sidebar-accent-foreground font-semibold text-sm hover:opacity-90 transition-opacity"
                    >
                        {userName?.charAt(0).toUpperCase() || "U"}
                    </button>
                </div>
            ) : (
                <div className="shrink-0 p-4 border-t border-sidebar-muted">
                    <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-sidebar-accent text-sidebar-accent-foreground font-semibold text-sm flex-shrink-0">
                            {userName?.charAt(0).toUpperCase() || "U"}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col gap-y-1.5">
                            {/* Name row with Settings and Logout on the same line */}
                            <div className="flex items-center gap-2 flex-nowrap">
                                <p className="text-sm font-medium min-w-0 flex-1 break-words">
                                    {userName || "User"}
                                </p>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    {onSettingsClick && (
                                        <button
                                            onClick={onSettingsClick}
                                            className="p-1.5 text-sidebar-muted-foreground hover:text-sidebar-foreground rounded-lg hover:bg-sidebar-muted transition-colors duration-200"
                                            title="Settings"
                                        >
                                            <Settings className="h-4 w-4" />
                                        </button>
                                    )}
                                    {onLogout && (
                                        <button
                                            onClick={onLogout}
                                            className="p-1.5 text-sidebar-muted-foreground hover:text-sidebar-foreground rounded-lg hover:bg-sidebar-muted transition-colors duration-200"
                                            title="Logout"
                                        >
                                            <LogOut className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Row 2: Company + icon (whole row clickable) */}
                            <div
                                className="flex min-w-0 items-center gap-1.5 flex-nowrap py-0.5"
                                data-testid="sidebar-company"
                            >
                                {canSwitchCompany && onCompanySwitch ? (
                                    <InlineSwitcher
                                        options={companyOptions}
                                        value={effectiveCompanyId || null}
                                        onChange={onCompanySwitch}
                                        renderTrigger={() => (
                                            <span
                                                className="inline-flex min-w-0 items-center gap-2 flex-nowrap py-0.5"
                                                title={displayCompanyName || undefined}
                                            >
                                                <span className="min-w-0 text-sm text-sidebar-muted-foreground truncate whitespace-nowrap">
                                                    {displayCompanyName || "No company"}
                                                </span>
                                                <Repeat
                                                    className="h-4 w-4 flex-shrink-0"
                                                    aria-hidden
                                                />
                                            </span>
                                        )}
                                    />
                                ) : (
                                    <p className="text-sm text-sidebar-muted-foreground truncate">
                                        {displayCompanyName || "No company"}
                                    </p>
                                )}
                            </div>
                            {/* Row 3: Subgroup + icon (label and icon stay on one line) */}
                            <div
                                className="flex items-center gap-2 flex-nowrap py-0.5"
                                data-testid="sidebar-subgroup"
                            >
                                {canSwitchSubgroup && onSubgroupSwitch ? (
                                    <InlineSwitcher
                                        options={subgroupOptions}
                                        value={currentSubgroup}
                                        onChange={(sg) =>
                                            onSubgroupSwitch(sg === userSubgroup ? null : sg)
                                        }
                                        renderTrigger={() => (
                                            <span className="inline-flex items-center gap-2 flex-nowrap py-0.5">
                                                <span className="text-sm text-sidebar-muted-foreground whitespace-nowrap">
                                                    {currentSubgroupLabel}
                                                </span>
                                                <Repeat
                                                    className="h-4 w-4 flex-shrink-0"
                                                    aria-hidden
                                                />
                                            </span>
                                        )}
                                    />
                                ) : (
                                    <p className="text-sm text-sidebar-muted-foreground">
                                        {currentSubgroupLabel}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </aside>
    );
}
